import React, { useState } from 'react'
import AdxSortTable from 'components/reusable/table/AdxSortTable'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import CardMedia from '@mui/material/CardMedia'
import TextField from '@mui/material/TextField'
import { useLocation, useNavigate } from 'react-router-dom'
import StepperAvatar from 'components/reusable/AdxProgressStepper/StepperAvatar'
import ClaimProgressTooltip from './ClaimProgressTooltip'
import defaultImage from 'assets/images/default_avatar.png'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import AdxButton from 'components/custom/AdxButton'
import { FileDownload, PictureAsPdf, TableChart } from '@mui/icons-material'
import { exportClaimsToPdf, exportClaimsToExcel } from 'services/api/modules/claim/approval-center/report'
import { Menu, MenuItem as MenuItemComponent } from '@mui/material'

const ClaimRespondedTab = ({
  respondedData,
  error,
  pagination,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}) => {
  const [isExporting, setIsExporting] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const hasAppliedDateRange = Boolean(dateFrom && dateTo)
  const hasIncompleteDateRange = Boolean(dateFrom || dateTo) && !hasAppliedDateRange
  const hasInvalidDateRange = hasIncompleteDateRange || Boolean(dateFrom && dateTo && dateFrom > dateTo)
  const dateFieldSx = {
    width: 170,
    '& .MuiInputLabel-root': {
      bgcolor: 'white.main',
      px: 0.5,
    },
  }

  const handleExportMenuClose = () => {
    setAnchorEl(null)
  }

  const handleFormatSelect = async (format) => {
    handleExportMenuClose()
    if (hasInvalidDateRange) return
    
    // Trigger export immediately after format selection
    setIsExporting(true)
    try {
      const exportQuery = hasAppliedDateRange ? { dateFrom, dateTo } : {}

      if (format === 'pdf') {
        await exportClaimsToPdf(exportQuery)
      } else {
        await exportClaimsToExcel(exportQuery)
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const upcomingRequestColumn = [
    {
      Header: 'Request',
      accessor: 'title',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            columnGap={3}
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={50}
                height={50}
                bgcolor="alternate.light"
                borderRadius={'50%'}
              >
                <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>article</Icon>
              </Box>
              <Box>
                <Typography
                  width={{
                    xs: 'calc(100vw - 400px)',
                    lg: 'calc(100vw - 1200px)',
                  }}
                  minWidth={{ xs: 1, lg: '200px' }}
                  noWrap
                  variant="body1"
                  color="dark-text.main "
                  textTransform="capitalize"
                >
                  {data.title}
                </Typography>
                <Box display={'flex'} alignItems={'center'} columnGap={1.5}>
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data.claim_report_type}
                  </Typography>

                  <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle_rounded</Icon>

                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data.start_date} - {data.end_date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Reimburse amount',
      accessor: 'amount_payable',

      Cell: (props) => {
        const data = props.row.original

        return (
          <Box display={'flex'} flexDirection="column" justifyContent="center" rowGap={0.5}>
            <Typography variant="body1" color="dark-text.main" align="left" noWrap>
              {`RM ${parseFloat(data.amount_payable).toFixed(2)}`}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Requester',
      accessor: 'staff_name',
      Cell: (props) => {
        const data = props.row.original

        return (
          <Box display={'flex'} alignItems="center" columnGap={1}>
            <CardMedia
              component="img"
              sx={{
                height: 52,
                width: 52,
                bgcolor: 'gray-background.main',
                objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'white.main',
              }}
              alt={data.staff_name}
              src={data.staff_img || defaultImage}
            />

            <Typography variant="body1" color="dark-text.main" align="left" maxWidth={150} noWrap>
              {data.staff_name}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Progress',
      accessor: 'currentProgress.status',
      Cell: (props) => {
        const data = props.row.original

        return (
          <ClaimProgressTooltip
            data={{
              members: data?.report_progress?.members,
              status: data?.report_status?.name,
            }}
          >
            <Box
              height="52px"
              width="52px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <StepperAvatar
                name={data?.report_progress?.members}
                imgUrl={data?.report_progress?.profilePics}
                active
                sxAvatar={{ borderColor: 'white.main' }}
                disableToolTip
              />
            </Box>
          </ClaimProgressTooltip>
        )
      },
    },
  ]

  const navigate = useNavigate()
  const backgroundLocation = useLocation()
  const hasRespondedClaims = (respondedData || []).length > 0

  return (
    <Box>
      {/* Export Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          p: 2,
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderBottom: 0,
          borderTopLeftRadius: '4.5px',
          borderTopRightRadius: '4.5px',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          label="From"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => onDateFromChange(e.target.value)}
          error={hasInvalidDateRange}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />

        <TextField
          label="To"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => onDateToChange(e.target.value)}
          error={hasInvalidDateRange}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdxButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={isExporting || hasInvalidDateRange}
            isLoading={isExporting}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FileDownload />
              <Typography>{isExporting ? 'Exporting...' : 'Export Claims'}</Typography>
            </Box>
          </AdxButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItemComponent onClick={() => handleFormatSelect('pdf')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdf />
                <Typography>Export as PDF</Typography>
              </Box>
            </MenuItemComponent>
            <MenuItemComponent onClick={() => handleFormatSelect('excel')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChart />
                <Typography>Export as Excel</Typography>
              </Box>
            </MenuItemComponent>
          </Menu>
        </Box>
      </Box>

      {hasRespondedClaims ? (
        <AdxSortTable
          height="calc(100vh - 320px)"
          stickyHeader
          divClick
          onClick={(e) =>
            navigate(`/app/approval-center/claim/request/${e.id}`, {
              state: {
                backgroundLocation,
                data: e,
              },
            })
          }
          column={upcomingRequestColumn}
          data={respondedData}
          pagination={Boolean(pagination)}
          count={pagination?.total}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            marginRight: '10px',
            boxShadow: null,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      ) : (
        <AdxEmptyStateV2
          title={
            error?.status === 403
              ? 'You are not authorized to access this feature. Please contact your admin for access.'
              : hasAppliedDateRange
              ? 'No approved claim request for selected date range'
              : 'No approved claim request'
          }
        />
      )}
    </Box>
  )
}

export default ClaimRespondedTab
